import {Button, Input, rem} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Anchor} from 'src/design/atoms/Anchor';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Collapse} from 'src/design/atoms/Collapse';
import {Group} from 'src/design/atoms/Group';
import {List} from 'src/design/atoms/List';
import {Loader} from 'src/design/atoms/Loader';
import {Text} from 'src/design/atoms/Text';
import type {FileRejection, FileWithPath} from '@mantine/dropzone';
import {Dropzone} from '@mantine/dropzone';
import {notifications} from '@mantine/notifications';
import {
  IconExclamationCircle,
  IconFileFilled,
  IconPhoto,
  IconUpload,
  IconX,
} from '@tabler/icons-react';
import {nanoid} from 'nanoid';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {DeleteActionIcon} from 'src/design/molecules/DeleteActionIcon';
import {isEffectivelyOffline} from 'src/offline/connectivity';
import {useOfflineCaptureEnabled} from 'src/app/forms/offline/OfflineCaptureContext';
import {
  deleteCapturedFormFiles,
  storeCapturedFormFile,
} from 'src/app/forms/offline/localFiles';
import {useAlert} from 'src/shared/hooks/useAlert';
import {
  isResizableImage,
  resizeImage,
} from 'src/shared/utils/imageResize';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IFormFieldV2} from 'src/types/FormV2';
import {uploadFileBatch} from './utils/uploadFileBatch';

declare module 'src/types/FormV2' {
  interface IFormFieldV2Validation {
    allowedFileTypes?: string[];
    allowMultiple?: boolean;
  }
}

export interface IFormDocumentUploadFile {
  name: string;
  value: string;
  encodedUrl: string;
  encodedKey: string;
  file?: File;
  success: boolean;
  type?: string;
  /** Captured offline; the blob lives in the outbox and uploads on reconnect. */
  pending?: boolean;
  /** Stable key linking a pending placeholder to its stored blob. */
  refKey?: string;
}

export const DEFAULT_MAX_FILESIZE = 50e6;

export interface FormFileUploadV2Props {
  field: IFormFieldV2;
  value: any; // Keeping as any for legacy support
  error?: string;
  onChange: (
    name: string,
    value: any,
    uploadedFiles: IFormDocumentUploadFile[]
  ) => void;
}

export const FormFileUploadV2 = React.memo<FormFileUploadV2Props>(
  ({field, value, error, onChange}: FormFileUploadV2Props) => {
    const {setAlert} = useAlert();
    const offlineCaptureEnabled = useOfflineCaptureEnabled();
    const [rejectedFiles, setRejectedFiles] = useState<FileRejection[]>([]);
    const filesToProcessRef = useRef<FileWithPath[]>([]);
    const [uploading, setUploading] = useState(false);

    // Parse the value prop which could be a string or array
    const safeFileArrayParse = useCallback(
      (value: any): IFormDocumentUploadFile[] => {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        try {
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      },
      []
    );

    const uploadedFiles = useMemo(
      () => safeFileArrayParse(value),
      [safeFileArrayParse, value]
    );
    const uploadedFileNames = useMemo(
      () => uploadedFiles.map(file => file.name),
      [uploadedFiles]
    );

    const handleFilesDrop = useCallback(
      async (files: FileWithPath[]) => {
        const optimizedFiles = await Promise.all(
          files.map(async base => {
            if (isResizableImage(base)) {
              try {
                return await resizeImage(base);
              } catch {
                // Safety valve: never lose an evidence photo on resize failure.
                notifications.show({
                  color: 'red',
                  title: 'Unable to optimize image',
                  message:
                    'Please contact support and provide a copy of your image file.',
                });
              }
            }
            return base;
          })
        );

        const uniqueFiles = optimizedFiles
          .map(file => {
            if (!file) return null;
            let newName = file.name;
            const fileExtension = newName.substring(newName.lastIndexOf('.'));

            while (uploadedFileNames.includes(newName)) {
              newName =
                newName.replace(fileExtension, '') +
                `-${nanoid(5)}` +
                fileExtension;
            }

            return new File([file], newName, {type: file.type});
          })
          .filter(Boolean) as File[];

        filesToProcessRef.current = uniqueFiles;
        setUploading(true);
      },
      [uploadedFileNames]
    );

    const captureFileBatch = useCallback(
      (batch: FileWithPath[]) =>
        Promise.all(
          batch.map(async currentFile => {
            try {
              const refKey = await storeCapturedFormFile(
                currentFile,
                currentFile.name,
                currentFile.type
              );
              if (!refKey) {
                throw new Error(
                  'No offline session is available to hold this file.'
                );
              }
              return {
                name: currentFile.name,
                file: currentFile,
                value: '',
                success: false,
                encodedUrl: '',
                encodedKey: '',
                type: currentFile.type,
                pending: true,
                refKey,
              } as IFormDocumentUploadFile;
            } catch (error) {
              setAlert(parseErrorMessage(error), 'error');
              return {
                name: currentFile.name,
                success: false,
              } as IFormDocumentUploadFile;
            }
          })
        ),
      [setAlert]
    );

    useEffect(() => {
      const process = async () => {
        const offline = offlineCaptureEnabled && isEffectivelyOffline();
        const files = offline
          ? await captureFileBatch(filesToProcessRef.current)
          : await uploadFileBatch(filesToProcessRef.current, setAlert);

        filesToProcessRef.current = [];
        setUploading(false);

        // A pending offline capture is not a failure — it uploads on reconnect.
        const errors = files.filter(i => i.success === false && !i.pending);
        if (errors.length > 0) {
          setAlert(
            `File(s) failed to upload: ${errors.map(e => e.name)}`,
            'error'
          );
        }

        const uploaded = files.filter(i => i.success === true);
        const pendingUploads = files.filter(i => i.pending === true);

        if (uploaded.length > 0 || pendingUploads.length > 0) {
          const updatedFiles = [...uploadedFiles, ...uploaded, ...pendingUploads];
          // Convert to JSON string for legacy support
          onChange(field.name, JSON.stringify(updatedFiles), updatedFiles);
        }

        if (pendingUploads.length > 0) {
          setAlert(
            `File(s) saved offline and will upload when you reconnect: ${pendingUploads
              .map(e => e.name)
              .join(', ')}`,
            'info'
          );
        }

        if (
          !field.fileUploadOptions?.isHideUploadSuccessEnabled &&
          uploaded.length > 0
        ) {
          setAlert(
            `File(s) successfully uploaded: ${uploaded
              .map(e => e.name)
              .join(', ')}`,
            'success'
          );
        }
      };

      if (uploading && filesToProcessRef.current.length > 0) {
        void process();
      }
    }, [uploading]);

    const handleRemoveFile = useCallback(
      (fileName: string) => {
        // Two pending files can share a name, so delete every captured blob for it.
        const capturedRefKeys = uploadedFiles
          .filter(f => f.name === fileName && f.pending && f.refKey)
          .map(f => f.refKey!);
        if (capturedRefKeys.length > 0) {
          void deleteCapturedFormFiles(capturedRefKeys);
        }
        const updatedFiles = uploadedFiles.filter(f => f.name !== fileName);
        onChange(field.name, JSON.stringify(updatedFiles), updatedFiles);
      },
      [uploadedFiles, field.name, onChange]
    );

    const handleViewFile = useCallback((file: IFormDocumentUploadFile) => {
      if (!file.file) return;
      const url = URL.createObjectURL(file.file);
      window.open(url, '_blank');
    }, []);

    const shouldShowDropzone =
      !field.fileUploadOptions?.simplifiedUploader ||
      uploadedFiles.length === 0;
    const compact = field.fileUploadOptions?.compact === true;
    const showFileStatus =
      !compact ||
      uploading ||
      uploadedFiles.length > 0 ||
      rejectedFiles.length > 0;

    return (
      <Input.Wrapper
        data-testid={`form-field-${field.name}`}
        label={field.label}
        description={field.description}
        error={error}
        withAsterisk={field.required}
        inputWrapperOrder={['label', 'error', 'description', 'input']}>
        <Collapse in={shouldShowDropzone}>
          <Dropzone
            mt="md"
            disabled={uploading || field.disabled}
            onDrop={handleFilesDrop}
            onReject={setRejectedFiles}
            maxSize={
              field.fileUploadOptions?.maxFileSize ?? DEFAULT_MAX_FILESIZE
            }
            accept={field.fileUploadOptions?.allowedFileTypes}
            multiple={field.fileUploadOptions?.allowMultiple ?? false}>
            <Box mih={compact ? 72 : 150} style={{pointerEvents: 'none'}}>
              <Center mb={compact ? 0 : 'md'} h={compact ? 72 : undefined}>
                <Dropzone.Accept>
                  <IconUpload
                    style={{
                      width: rem(compact ? 28 : 52),
                      height: rem(compact ? 28 : 52),
                      color: 'var(--mantine-color-blue-6)',
                      marginRight: compact ? rem(10) : undefined,
                    }}
                    stroke={1.5}
                  />
                </Dropzone.Accept>
                <Dropzone.Reject>
                  <IconX
                    style={{
                      width: rem(compact ? 28 : 52),
                      height: rem(compact ? 28 : 52),
                      color: 'var(--mantine-color-red-6)',
                      marginRight: compact ? rem(10) : undefined,
                    }}
                    stroke={1.5}
                  />
                </Dropzone.Reject>
                <Dropzone.Idle>
                  <IconPhoto
                    style={{
                      width: rem(compact ? 28 : 52),
                      height: rem(compact ? 28 : 52),
                      color: 'var(--mantine-color-dimmed)',
                      marginRight: compact ? rem(10) : undefined,
                    }}
                    stroke={1.5}
                  />
                </Dropzone.Idle>
                {compact && (
                  <Text size="sm" inline>
                    Drop a file here or click to select
                  </Text>
                )}
              </Center>

              {!compact && (
                <Center>
                  <Box>
                    <Text size="xl" ta="center" inline>
                      Drag files here or click to select files
                    </Text>
                    <List mt="md" mr="xs" size="sm" c="dimmed">
                      <List.Item>
                        {field.fileUploadOptions?.allowMultiple
                          ? 'Attach multiple files'
                          : 'Attach a single file'}
                      </List.Item>
                      <List.Item>
                        Files should not exceed{' '}
                        {Math.round(
                          (field.fileUploadOptions?.maxFileSize ??
                            DEFAULT_MAX_FILESIZE) * 1e-6
                        )}{' '}
                        MB
                      </List.Item>
                      <List.Item>
                        {field.fileUploadOptions?.allowedFileTypes
                          ? `Allowed file types: ${field.fileUploadOptions.allowedFileTypes.join(
                              ', '
                            )}`
                          : 'Common file types accepted (images, documents, spreadsheets)'}
                      </List.Item>
                      {field?.fileUploadOptions?.showFileSizeWarning && (
                        <List.Item>
                          {`If working with larger files (over 50 MB), it may take a moment to upload. Please be patient.`}
                        </List.Item>
                      )}
                    </List>
                  </Box>
                </Center>
              )}
            </Box>
          </Dropzone>
        </Collapse>

        {showFileStatus && (
          <Box mt="md" mb={compact ? 0 : 'xl'}>
            {uploading &&
              filesToProcessRef.current.map(file => (
                <Group key={file.name} justify="space-between" ml="md" mr="md">
                  <Loader size="sm" />
                  <Text size="sm">Uploading... {file.name}</Text>
                </Group>
              ))}

            {uploadedFiles.map(file => (
              <Group key={file.name} justify="space-between" ml="md" mr="md">
                <Group>
                  <IconFileFilled size={24} />
                  <Anchor
                    size="sm"
                    c="dimmed"
                    onClick={() => handleViewFile(file)}>
                    {file.name}
                  </Anchor>
                  {file.pending && (
                    <Text size="xs" c="dimmed">
                      (saved offline — uploads on reconnect)
                    </Text>
                  )}
                </Group>
                <DeleteActionIcon
                  onClick={() => handleRemoveFile(file.name)}
                  tooltipText="Remove file"
                />
              </Group>
            ))}

            {uploadedFiles.length > 1 && (
              <Group justify="end" mt="md" ml="md" mr="md">
                <Button
                  size="compact-xs"
                  variant="subtle"
                  color="red"
                  onClick={() => onChange(field.name, JSON.stringify([]), [])}>
                  Remove all
                </Button>
              </Group>
            )}

            {rejectedFiles.length > 0 && (
              <Alert
                mt="md"
                color="red"
                icon={<IconExclamationCircle />}
                title="The following files were rejected:"
                withCloseButton
                onClose={() => setRejectedFiles([])}>
                <List c="dimmed" size="sm">
                  {rejectedFiles.map(rejected => (
                    <List.Item key={rejected.file.name}>
                      {rejected.file.name}
                      <List withPadding c="dimmed" size="sm">
                        {rejected.errors.map(
                          (error: {message: string}, index: number) => (
                            <List.Item key={index}>{error.message}</List.Item>
                          )
                        )}
                      </List>
                    </List.Item>
                  ))}
                </List>
              </Alert>
            )}

            {uploadedFiles.length === 0 && !uploading && !compact && (
              <Group justify="center" mt="xl">
                <Text c="dimmed" size="sm">
                  No files uploaded
                </Text>
              </Group>
            )}
          </Box>
        )}
      </Input.Wrapper>
    );
  }
);
